import type { IDottedBackgroundProps } from '@components/layout/DottedBackground';
import { Box } from '@mui/material';
import clsx from 'clsx';
import { createContext, useContext, useLayoutEffect, useState } from 'react';

const CONTENT_SCROLL_CLASSNAME = 'min-h-0 flex-1 overflow-auto h-full';
const CONTENT_MAX_WIDTH_CLASSNAME = 'max-w-[1440px] w-full mx-auto h-full';
const DOTTED_BACKGROUND_COLOR = '#FAF9F5';
const DOTTED_BACKGROUND_DOT_COLOR = '#b1b5b9';
const DOTTED_BACKGROUND_SIZE = '20px';
const DOTTED_BACKGROUND_DOT_SIZE = '2px';

const MaxWidthWrapperScrollContext = createContext<HTMLDivElement | null>(null);
const MaxWidthWrapperBottomContext = createContext<HTMLDivElement | null>(null);

export const useMaxWidthWrapperScrollElement = () => useContext(MaxWidthWrapperScrollContext);
export const useMaxWidthWrapperBottomElement = () => useContext(MaxWidthWrapperBottomContext);

const resetScrollElement = (scrollElement: Pick<HTMLElement, 'scrollTop'> | null) => {
    if (scrollElement) {
        scrollElement.scrollTop = 0;
    }
};

export const MaxWidthWrapper = ({
    children,
    dottedBackgroundProps,
    fullWidthTopContent,
    scrollResetKey,
}: {
    children: React.ReactNode;
    dottedBackgroundProps?: IDottedBackgroundProps;
    fullWidthTopContent?: React.ReactNode;
    scrollResetKey?: string;
}) => {
    const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
    const [bottomElement, setBottomElement] = useState<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        if (scrollResetKey !== undefined) {
            resetScrollElement(scrollElement);
        }
    }, [scrollElement, scrollResetKey]);

    return (
        <MaxWidthWrapperScrollContext.Provider value={scrollElement}>
            <MaxWidthWrapperBottomContext.Provider value={bottomElement}>
                <Box className="flex h-full min-h-0 w-full flex-col overflow-hidden">
                    <Box
                        ref={setScrollElement}
                        className={clsx(
                            CONTENT_SCROLL_CLASSNAME,
                            dottedBackgroundProps && `dotted-background-${dottedBackgroundProps.color ?? 'cultured'}`,
                            dottedBackgroundProps?.className
                        )}
                        style={
                            dottedBackgroundProps
                                ? {
                                      backgroundAttachment: 'fixed',
                                      ...(dottedBackgroundProps.gradient
                                          ? {
                                                backgroundImage: [
                                                    `linear-gradient(to top, transparent, ${DOTTED_BACKGROUND_COLOR} ${
                                                        dottedBackgroundProps.gradientValue ?? '10%'
                                                    })`,
                                                    `linear-gradient(90deg, ${DOTTED_BACKGROUND_COLOR} calc(${DOTTED_BACKGROUND_SIZE} - ${DOTTED_BACKGROUND_DOT_SIZE}), transparent 1%)`,
                                                    `linear-gradient(${DOTTED_BACKGROUND_COLOR} calc(${DOTTED_BACKGROUND_SIZE} - ${DOTTED_BACKGROUND_DOT_SIZE}), transparent 1%)`,
                                                ].join(', '),
                                                backgroundColor: DOTTED_BACKGROUND_DOT_COLOR,
                                                backgroundSize: `auto, ${DOTTED_BACKGROUND_SIZE} ${DOTTED_BACKGROUND_SIZE}, ${DOTTED_BACKGROUND_SIZE} ${DOTTED_BACKGROUND_SIZE}`,
                                                backgroundPosition: '0 0, center, center',
                                                backgroundRepeat: 'no-repeat, repeat, repeat',
                                            }
                                          : {}),
                                  }
                                : undefined
                        }
                    >
                        {fullWidthTopContent}
                        <div className={CONTENT_MAX_WIDTH_CLASSNAME}>{children}</div>
                    </Box>
                    <div ref={setBottomElement} className="shrink-0" />
                </Box>
            </MaxWidthWrapperBottomContext.Provider>
        </MaxWidthWrapperScrollContext.Provider>
    );
};
